import { FC, useContext, useEffect } from 'react';
import { AvatarIcon, CalendarIcon, EditIconBlue, SourceIcon } from 'assets/icons';
import DateUtils from 'utils/dateUtils';
import { Job } from '../jobs.model';
import JobTagsManager from 'containers/jobs/job-details/components/JobTagsManager';
import { useLazyGetTagsQuery } from 'containers/tags/api';
import { useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { JobPermissionsContext } from '../job-details/JobDetails';
import { JobLeadSourceTypes } from '../constant';

interface JobDetailsOverviewSectionProps {
  job: Job;
  isSingleMemberTeam?: boolean;
  onEditScheduleClick: () => void;
  onEditAssigneesClick: () => void;
  onEditLeadSourceClick: () => void;
  onEditExternalCompanyClick: () => void;
  onEditDescriptionClick?: () => void;
}

const JobDetailsOverviewSection: FC<JobDetailsOverviewSectionProps> = ({
  job,
  isSingleMemberTeam,
  onEditScheduleClick,
  onEditAssigneesClick,
  onEditLeadSourceClick,
  onEditExternalCompanyClick,
  onEditDescriptionClick
}) => {
  const [getJobTagsQuery, { data: tags, isLoading: isLoadingTags }] = useLazyGetTagsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { canEditJobs, canEditJobSource, canViewJobSource } = useContext(JobPermissionsContext);

  // Methods from JobPanel
  const getTeamMemberNames = () => {
    let remainingMemberCount = 0;
    if (job.assignees?.length > 0) {
      let teamMemberNames = '';

      for (let index = 0; index < job.assignees.length; index++) {
        const nameToAdd = job.assignees[index].name;
        if (teamMemberNames.length < 27) {
          teamMemberNames += `${teamMemberNames.length > 0 ? ', ' : ''}${nameToAdd}`;
        } else {
          remainingMemberCount = job.assignees.length - index;
          break;
        }
      }

      return (
        <div className="flex items-center">
          <div className="max-w-[190px] overflow-hidden text-ellipsis whitespace-nowrap">
            {`${teamMemberNames.trim()}`}
          </div>
          {remainingMemberCount > 0 && <div className="ml-1 text-secondaryBtnText">{`+${remainingMemberCount}`}</div>}
        </div>
      );
    }
    return '';
  };

  useEffect(() => {
    getJobTagsQuery({ teamId: currentTeam?.id, isActive: true });
  }, [currentTeam?.id]);

  return (
    <div className="flex flex-col space-y-6 pt-5">
      <div className="text-[17px] font-bold text-primaryText">Job details</div>

      {/* Main sections */}
      <div className="flex flex-col space-y-6">
        {/* First row */}
        <div className="grid grid-cols-3 gap-10">
          {/* Schedule Section */}
          <div className="flex flex-col space-y-1.5">
            <div className="flex items-center gap-2">
              <CalendarIcon className="path-fill-current path h-[18px] w-[18px] text-primaryText" />
              <div className="text-sbase font-semibold text-primaryText">Schedule</div>
              {canEditJobs && (
                <div className="cursor-pointer" onClick={onEditScheduleClick}>
                  <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
                </div>
              )}
            </div>
            <div className="text-sbase text-primaryText">
              {job.isScheduled
                ? `${DateUtils.getDateInFormat({ date: new Date(job.scheduledFrom), dateFormat: 'MMM dd, yyyy' })}, 
                  ${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`
                : '-'}
            </div>
          </div>

          {/* Assigned to Section */}
          <div className="flex flex-col space-y-1.5">
            <div className="flex items-center gap-2">
              <AvatarIcon />
              <div className="text-sbase font-semibold text-primaryText">Assigned to</div>
              {canEditJobs && (
                <div
                  className={`cursor-pointer ${isSingleMemberTeam ? 'pointer-events-none' : ''}`}
                  onClick={() => !isSingleMemberTeam && onEditAssigneesClick()}>
                  <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
                </div>
              )}
            </div>
            <div className="text-sbase text-primaryText">{getTeamMemberNames() || '-'}</div>
          </div>
          {canViewJobSource && (
            <>
              <div className="col-span-1"></div>

              {/* Lead source Section */}
              <div className="flex flex-col space-y-1.5">
                <div className="flex items-center gap-2">
                  <SourceIcon />
                  <div className="text-sbase font-semibold text-primaryText">Lead source</div>
                  {canEditJobs && canEditJobSource && (
                    <div className="cursor-pointer" onClick={onEditLeadSourceClick}>
                      <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
                    </div>
                  )}
                </div>
                <div className="text-sbase text-primaryText">
                  {`${
                    job?.leadSourceType
                      ? JobLeadSourceTypes?.find(item => item.value === job?.leadSourceType)?.label || '-'
                      : '-'
                  } ${job?.leadSource ? `- ${job?.leadSource}` : ''}`}
                </div>
              </div>

              {/* External company Section */}
              <div className="flex flex-col space-y-1.5">
                <div className="flex items-center gap-2">
                  <SourceIcon />
                  <div className="text-sbase font-semibold text-primaryText">External company</div>
                  {canEditJobs && canEditJobSource && (
                    <div className="cursor-pointer" onClick={onEditExternalCompanyClick}>
                      <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
                    </div>
                  )}
                </div>
                <div className="text-sbase text-primaryText">{job?.externalCompany?.name || '-'}</div>
              </div>
              <div className="col-span-1"></div>
            </>
          )}
        </div>
      </div>

      {/* Bottom info section */}
      <div className="grid grid-cols-3 gap-10">
        {/* Reference ID */}
        <div className="flex flex-col space-y-1.5">
          <div className="text-sbase font-semibold text-accentText">Reference ID</div>
          <div className="text-sbase text-primaryText">{job.referenceNo || '-'}</div>
        </div>

        {/* Job Created On */}
        <div className="flex flex-col space-y-1.5">
          <div className="text-sbase font-semibold text-accentText">Job created on</div>
          <div className="text-sbase text-primaryText">
            {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
          </div>
        </div>

        {/* Job Created By */}
        <div className="flex flex-col space-y-1.5">
          <div className="text-sbase font-semibold text-accentText">Job created by</div>
          <div className="text-sbase text-primaryText">{job.createdBy?.name || '-'}</div>
        </div>
      </div>

      {/* Description Section */}
      <div className="flex flex-col border-t border-gray-200 pt-5">
        <div className="flex items-center gap-2">
          <div className="text-[17px] font-bold text-primaryText">Description</div>
          {canEditJobs && (
            <div className="cursor-pointer" onClick={onEditDescriptionClick}>
              <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
            </div>
          )}
        </div>
        <div className="mt-1 text-sbase text-primaryText">{job.description || 'No description provided.'}</div>
      </div>

      {/* Tags Section */}
      <div className="flex flex-col border-t border-gray-200 pt-5">
        <div className="flex flex-col gap-2">
          <div className="text-17px font-bold text-primaryText">Tags</div>
          <div className="flex flex-wrap items-center gap-1">
            <JobTagsManager
              tags={tags?.data?.tags || []}
              jobTags={job.jobTags || []}
              jobNumber={job.jobNumber}
              isLoadingTags={isLoadingTags}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsOverviewSection;
